import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { useAuthContext } from "../../contexts/AuthContext";
import { useToast } from "../ui/use-toast";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  User,
  Briefcase,
  Trash2,
  MoveDown,
  MoveUp,
  Phone,
  ArrowUpCircle,
} from "lucide-react";
import DraggableField from "./draggable-field";
import AddRecipientDialog from "./add-recipient-dialog";
import { usePlan } from "../../contexts/PlanContext";
import { Link } from "react-router-dom";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Recipient } from "../../types/document-types";

interface DocumentSidebarProps {
  onRecipientsChange?: (recipients: Recipient[]) => void;
  onWorkflowChange?: (workflow: "parallel" | "sequential") => void;
  existingRecipients?: Recipient[];
  initialWorkflow?: "parallel" | "sequential";
}

export default function DocumentSidebar({
  onRecipientsChange,
  onWorkflowChange,
  existingRecipients: recipients = [],
  initialWorkflow = "parallel",
}: DocumentSidebarProps) {
  const { t } = useTranslation(['documents', 'common', 'billing']);
  const { canUse } = usePlan();
  const canUseSms = canUse('sms_region');

  const [isAddRecipientDialogOpen, setIsAddRecipientDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<{
    name: string;
    email: string;
    manager_name?: string;
    manager_email?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [signingWorkflow, setSigningWorkflow] = useState<"parallel" | "sequential">(initialWorkflow);
  
  // Use AuthContext to get user data
  const { user } = useAuthContext();
  const { toast } = useToast();

  // Get user data from auth context instead of making a separate API call
  useEffect(() => {
    if (user) {
      console.log("User data in document-sidebar:", user);
      console.log("Manager data in document-sidebar:", user.manager);
      
      setCurrentUser({
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        // Get manager data if it exists
        manager_name: user.manager ? `${user.manager.first_name} ${user.manager.last_name}` : "",
        manager_email: user.manager ? user.manager.email : "",
      });
      setIsLoading(false);
    }
  }, [user]);

  const addRecipient = (type: "me" | "manager") => {
    if (!currentUser) return;

    // Check if already added
    const emailToCheck =
      type === "me" ? currentUser.email : user?.manager?.email;
    const isAlreadyAdded = recipients.some(
      (recipient) => recipient.email === emailToCheck
    );

    if (isAlreadyAdded) {
      return;
    }

    let newRecipient;
    if (type === "me") {
      newRecipient = {
        id: user!.id.toString(), 
        name: currentUser.name,
        email: currentUser.email,
        fields: [],
        isExpanded: true,
        isCurrentUser: true,
      };
      if (onRecipientsChange) {
        onRecipientsChange([...recipients, newRecipient]);
      }
    } 
    else if (type === "manager") {      
      // First try to get the manager ID from the nested manager object
      let managerId = "";
      
      // Debug the manager data - show detailed structure
      console.log("Manager info:", {
        managerObject: user?.manager,
        managerId: user?.manager?.id,
        managerEmail: user?.manager?.email,
        managerName: user?.manager ? `${user.manager.first_name} ${user.manager.last_name}` : null
      });
      
      // Try different ways to get the manager ID with fallbacks
      if (user?.manager?.id) {
        managerId = String(user.manager.id); // Convert to string for consistency
        console.log("Using manager ID from user.manager:", managerId);
      } else {
        // Fallback to generating a unique ID
        managerId = `manager-${Date.now()}`;
        console.log("Using generated manager ID:", managerId);
      }
      
      newRecipient = {
        id: managerId,
        name: user?.manager ? `${user.manager.first_name} ${user.manager.last_name}` : currentUser?.manager_name || "Manager",
        email: user?.manager?.email || currentUser?.manager_email || "manager@example.com",
        fields: [],
        isExpanded: true,
        isManager: true, // Mark as manager for reference
      };
      if (onRecipientsChange) {
        onRecipientsChange([...recipients, newRecipient]);
      }
    }
  };

  const addManagerAsRecipient = () => {
    // Log user data to debug manager relationship
    console.log('Auth user object:', user);
    
    try {
      const localStorageUser = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('LocalStorage user data:', localStorageUser);
      
      // Try to find manager ID from multiple sources with fallbacks
      let managerId = '';
      let managerName = '';
      
      // Approach 1: Get from user.manager (relationship)
      if (user?.manager?.id) {
        console.log('Found manager ID from user.manager.id:', user.manager.id);
        managerId = String(user.manager.id); // Convert to string for consistency
        managerName = `${user.manager.first_name} ${user.manager.last_name}`;
      } 
      // Approach 2: Get from user.manager_id (direct field)
      else if (user && 'manager_id' in user) { // Use in operator for dynamic property check
        console.log('Found manager ID from user.manager_id:', user.manager_id);
        managerId = String(user.manager_id); // Convert to string
      }
      // Approach 3: Check localStorage as fallback
      else if (localStorageUser?.manager?.id) {
        console.log('Found manager ID from localStorage manager object:', localStorageUser.manager.id);
        managerId = String(localStorageUser.manager.id); // Convert to string
        managerName = `${localStorageUser.manager.first_name} ${localStorageUser.manager.last_name}`;
      }
      // Approach 4: Check manager_id in localStorage
      else if (localStorageUser && 'manager_id' in localStorageUser) {
        console.log('Found manager ID from localStorage manager_id field:', localStorageUser.manager_id);
        managerId = String(localStorageUser.manager_id); // Convert to string
      }
      // No manager found
      else {
        console.error('No manager ID found in any source!');
        console.log('User properties:', Object.keys(user || {}));
        console.log('LocalStorage user properties:', Object.keys(localStorageUser || {}));
        toast({
          title: "Error",
          description: "Could not find your manager. Please contact support.",
          variant: "destructive"
        });
        return;
      }
      
      console.log('Using manager ID:', managerId);
      
      // Now use the managerId to add as recipient
      if (!managerId) {
        toast({
          title: "Error",
          description: "Could not find your manager. Please contact support.",
          variant: "destructive"
        });
        return;
      }
      
      const newRecipient: Recipient = {
        id: managerId,
        name: managerName || "Manager",
        email: user?.manager?.email || currentUser?.manager_email || "manager@example.com",
        fields: [],
        isExpanded: true,
        isManager: true, // Mark as manager for reference
      };
      if (onRecipientsChange) {
        onRecipientsChange([...recipients, newRecipient]);
      }
    } catch (error) {
      console.error('Error adding manager as recipient:', error);
    }
  };

  const handleAddRecipient = (recipient: { name: string; email: string }) => {
    // Check if already added
    const isAlreadyAdded = recipients.some((r) => r.email === recipient.email);
    if (isAlreadyAdded) return;

    const newRecipient: Recipient = {
      id: Date.now().toString(),
      name: recipient.name,
      email: recipient.email,
      fields: [],
      isExpanded: true,
    };

    if (onRecipientsChange) {
      onRecipientsChange([...recipients, newRecipient]);
    }
  };

  const handleRemoveRecipient = (id: string) => {
    if (onRecipientsChange) {
      onRecipientsChange(recipients.filter(r => r.id !== id));
    }
  };

  const handleToggleRecipient = (id: string) => {
    if (onRecipientsChange) {
      onRecipientsChange(
        recipients.map((recipient) =>
          recipient.id === id
            ? { ...recipient, isExpanded: !recipient.isExpanded }
            : recipient
        )
      );
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0 || !onRecipientsChange) return;
    const newRecipients = [...recipients];
    [newRecipients[index], newRecipients[index - 1]] = [
      newRecipients[index - 1],
      newRecipients[index],
    ];
    onRecipientsChange(newRecipients);
  };

  const handleMoveDown = (index: number) => {
    if (index === recipients.length - 1 || !onRecipientsChange) return;
    const newRecipients = [...recipients];
    [newRecipients[index], newRecipients[index + 1]] = [
      newRecipients[index + 1],
      newRecipients[index],
    ];
    onRecipientsChange(newRecipients);
  };

  const handleSentViaChange = (recipientId: string, value: "email" | "sms" | "both") => {
    if (!onRecipientsChange) return;
    onRecipientsChange(
      recipients.map((r) =>
        r.id === recipientId ? { ...r, sent_via: value } : r
      )
    );
  };

  const handlePhoneChange = (recipientId: string, value: string) => {
    if (!onRecipientsChange) return;
    onRecipientsChange(
      recipients.map((r) =>
        r.id === recipientId ? { ...r, phone: value } : r
      )
    );
  };

  return (
    <div className="flex flex-col w-[300px] h-full overflow-hidden border-l">
      <div className="p-4 space-y-4 shrink-0">
        <div>
          <h2 className="font-medium text-lg">{t('documents:editor_recipients_title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('documents:editor_recipients_description')}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {!recipients.some(r => r.isCurrentUser || r.email === currentUser?.email) && (
            <Button
              onClick={() => addRecipient("me")}
              variant="outline"
              size="sm"
              disabled={isLoading}
            >
              <User className="h-4 w-4 mr-1.5 text-blue-600" />
              {t('documents:editor_me')}
            </Button>
          )}
          {(user?.manager || user?.manager_id) && !recipients.some(r => r.isManager || r.email === (user?.manager?.email || currentUser?.manager_email)) && (
            <Button
              onClick={addManagerAsRecipient}
              variant="outline"
              size="sm"
              disabled={isLoading}
            >
              <Briefcase className="h-4 w-4 mr-1.5 text-purple-600" />
              {t('documents:editor_manager')}
            </Button>
          )}
          <Button
            onClick={() => setIsAddRecipientDialogOpen(true)}
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {t('documents:editor_add')}
          </Button>
        </div>

        {recipients.length >= 2 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('documents:editor_signing_workflow')}</p>
            <RadioGroup
              value={signingWorkflow}
              onValueChange={(value: "parallel" | "sequential") => {
                setSigningWorkflow(value);
                if (onWorkflowChange) {
                  onWorkflowChange(value);
                }
              }}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="parallel" id="parallel" />
                <Label htmlFor="parallel">{t('documents:editor_workflow_parallel')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="sequential" id="sequential" />
                <Label htmlFor="sequential">{t('documents:editor_workflow_sequential')}</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        <Separator />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="space-y-3">
          {recipients.map((recipient, index) => (
            <div
              key={recipient.id}
              className="border border-border rounded-md"
            >
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                      {recipient.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">
                        {recipient.name}
                        {signingWorkflow === "sequential" && (
                          <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                            #{index + 1}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {recipient.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 min-w-[70px] justify-end">
                    {signingWorkflow === "sequential" && (
                      <div className="flex">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleMoveUp(index);
                          }}
                          disabled={index === 0}
                          className="h-6 w-6 p-0"
                        >
                          <MoveUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleMoveDown(index);
                          }}
                          disabled={index === recipients.length - 1}
                          className="h-6 w-6 p-0"
                        >
                          <MoveDown className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => handleRemoveRecipient(recipient.id)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => handleToggleRecipient(recipient.id)}
                    >
                      {recipient.isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              {recipient.isExpanded && (
                <>
                <Separator />
                <div className="p-3 space-y-3">
                  {/* Send via selector */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-muted-foreground">{t('documents:editor_send_via')}</Label>
                    <Select
                      value={recipient.sent_via || "email"}
                      onValueChange={(val: "email" | "sms" | "both") => handleSentViaChange(recipient.id, val)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">{t('documents:editor_send_via_email')}</SelectItem>
                        <SelectItem value="sms" disabled={!canUseSms}>{t('documents:editor_send_via_sms')}{!canUseSms ? ' ⬆' : ''}</SelectItem>
                        <SelectItem value="both" disabled={!canUseSms}>{t('documents:editor_send_via_both')}{!canUseSms ? ' ⬆' : ''}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Phone number field - shown when SMS is involved */}
                  {(recipient.sent_via === "sms" || recipient.sent_via === "both") && canUseSms && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">
                        {t('documents:editor_phone_label')} *
                      </Label>
                      <div className="flex items-center border rounded-md focus-within:ring-1 focus-within:ring-primary">
                        <div className="px-2 py-1.5">
                          <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <Input
                          type="tel"
                          placeholder={t('documents:editor_phone_placeholder')}
                          className="border-0 h-8 text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
                          value={recipient.phone || ""}
                          onChange={(e) => handlePhoneChange(recipient.id, e.target.value)}
                        />
                      </div>
                      {!recipient.phone?.trim() && (
                        <p className="text-xs text-destructive">{t('documents:editor_phone_required')}</p>
                      )}
                    </div>
                  )}

                  {/* Upgrade prompt for SMS */}
                  {!canUseSms && (recipient.sent_via === "sms" || recipient.sent_via === "both") && (
                    <div className="space-y-1">
                      <Input type="tel" disabled placeholder={t('documents:editor_phone_placeholder')} className="h-8 text-xs bg-muted cursor-not-allowed" />
                      <Link to="/settings?tab=billing" className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium">
                        <ArrowUpCircle className="h-3 w-3" />
                        {t('billing:upgrade.button', 'Upgrade to enable SMS')}
                      </Link>
                    </div>
                  )}

                  <Separator />

                  {/* Draggable fields */}
                  <div className="space-y-2">
                    <DraggableField
                      type="signature"
                      label={t('documents:editor_field_signature')}
                      recipientId={recipient.id}
                      iconColor="text-indigo-600"
                    />
                    <DraggableField
                      type="name"
                      label={t('documents:editor_field_name')}
                      recipientId={recipient.id}
                      iconColor="text-blue-600"
                    />
                    <DraggableField
                      type="position"
                      label={t('documents:editor_field_position')}
                      recipientId={recipient.id}
                      iconColor="text-orange-600"
                    />
                    <DraggableField
                      type="date"
                      label={t('documents:editor_field_date')}
                      recipientId={recipient.id}
                      iconColor="text-amber-600"
                    />
                    <DraggableField
                      type="email"
                      label={t('documents:editor_field_email')}
                      recipientId={recipient.id}
                      iconColor="text-teal-600"
                    />
                    <DraggableField
                      type="city"
                      label={t('documents:editor_field_city')}
                      recipientId={recipient.id}
                      iconColor="text-rose-600"
                    />
                    <DraggableField
                      type="initial"
                      label={t('documents:editor_field_initial')}
                      recipientId={recipient.id}
                      iconColor="text-violet-600"
                    />
                  </div>
                </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <AddRecipientDialog
        open={isAddRecipientDialogOpen}
        onOpenChange={setIsAddRecipientDialogOpen}
        onAddRecipient={handleAddRecipient}
        existingRecipients={recipients.map((r) => ({ name: r.name, email: r.email }))}
      />
    </div>
  );
}
